import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useEffect,
  useState,
} from "react";
import styles from "./edit-flight.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useEditFlightMutation, useGetFlightQuery } from "../flightApi";
import { useGetAllQuery } from "../../airBase/airBaseApi";
import { ITransport } from "../../../../../interfaces/ITransport";
import { useGetAllTransportQuery } from "../../../../../api/transportApi";
import { useGetAllContractsQuery } from "../../contracts/contractApi";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import { HHMM2min, min2HHMM, validateTime } from "../../../../../helpers/time";
import { PreloaderBar } from "../../../../../ui/preloaderBar/PreloaderBar";

dayjs.extend(customParseFormat);
dayjs.extend(duration);

type Props = {
  id: number;
  close: () => void;
};

type State = {
  errors?: {
    airbase_id?: string[];
    contract_id?: string[];
    number?: string[];
    application_date?: string[];
    date_start?: string[];
    date_end?: string[];
    transport_id?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  airbase_id: z.coerce.number({
    message: "Выберите значение из списка",
  }),
  contract_id: z.coerce.number().positive({
    message: "Выберите значение из списка",
  }),
  number: z.string().min(1, {
    message: "Введите номер заявки",
  }),
  application_date: z.string(),
  date_start: z.string().min(16, { message: "Введите дату и время вылета" }),
  date_end: z.string().min(16, { message: "Введите дату и времы прилета" }),
  transport_id: z.coerce.number({
    message: "Выберите воздушное судно из списка",
  }),
});

const Form: FC<Props> = ({ close, id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [numberOptions, setNumberOptions] = useState<ITransport[] | null>(null);
  const [type, setType] = useState<number | null>(null);
  const [typeOptions, setTypeOptions] = useState<ITransport[] | null>(null);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [diff, setDuration] = useState("");
  const [edit, { isError, isLoading, isSuccess }] = useEditFlightMutation();
  const {
    data: flight,
    isFetching: isFlightFetching,
    isError: isFlightError,
  } = useGetFlightQuery(Number(id));
  const {
    data: airBases,
    isError: isAirbaseError,
    isFetching: isAirbaseFetching,
  } = useGetAllQuery();
  const {
    data: transport,
    isFetching: isTransportFetching,
    isError: isTransportError,
  } = useGetAllTransportQuery();
  const {
    data: contracts,
    isError: isContractError,
    isFetching: isContractFetching,
  } = useGetAllContractsQuery();

  const numberHandler = (val: string) => {
    const result = transport?.find((unit) => unit.id === Number(val));
    if (result) {
      setType(result.type);
    }
  };
  const typeHandler = (val: string) => {
    if (!transport) return;
    const result = transport.filter((unit) => unit.type === Number(val));
    setType(Number(val));
    if (result.length) {
      setNumberOptions(result);
    } else {
      setNumberOptions(transport);
    }
  };

  const calcDuration = (start: string, end: string) => {
    if (!start || !end) return;

    const x = dayjs(start, "YYYY-MM-DDTHH:mm");
    const y = dayjs(end, "YYYY-MM-DDTHH:mm");
    const duration = dayjs.duration(y.diff(x));
    setDuration(duration.format("HH:mm"));
  };

  const dateStartHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDateStart(e.target.value);
    calcDuration(e.target.value, dateEnd);
  };
  const dateEndHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDateEnd(e.target.value);
    calcDuration(dateStart, e.target.value);
  };

  useEffect(() => {
    if (!transport) return;
    const keys = [] as number[];
    const options = [] as ITransport[];
    transport.forEach((unit) => {
      if (keys.includes(unit.type)) return;
      keys.push(unit.type);
      options.push(unit);
    });
    setNumberOptions(transport);
    setTypeOptions(options);
  }, [transport]);

  useEffect(() => {
    if (!flight) return;
    setType(flight.transport_type);
    setDateStart(flight.date_start)
    setDateEnd(flight.date_end)
    const duration = min2HHMM(flight.duration);
    setDuration(duration);
    
  }, [flight]);

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      airbase_id: formData.get("airbase_id"),
      contract_id: formData.get("contract_id"),
      number: formData.get("number"),
      application_date: formData.get("application_date"),
      date_start: formData.get("date_start"),
      date_end: formData.get("date_end"),
      transport_id: formData.get("transport_id"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }
    if (!type) return;

    const {
      airbase_id,
      contract_id,
      number,
      application_date,
      date_start,
      date_end,
      transport_id,
    } = validatedFields.data;

    if(new Date(date_end) < new Date(dateStart)) {
      setState({
        message: "Проверьте даты вылета и прилета"
      })
      return;
    }

    edit({
      id,
      airbase_id,
      contract_id,
      number,
      application_date,
      date_start,
      date_end,
      duration: HHMM2min(diff),
      transport_id,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Редактирование налета</div>
      <div className={styles.row}>
        <label htmlFor="name">Авиаотделение</label>
        {flight && airBases ? (
          <select
            className={styles.select}
            name="airbase_id"
            defaultValue={String(flight?.airbase_id)}
          >
            <option className={styles.option}>Выберите</option>
            {airBases.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.short_name}
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.skeleton}>Загрузка списка...</div>
        )}
        <div id="airbase_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.airbase_id &&
            state.errors.airbase_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="contract_id">Договор</label>
        {flight && contracts ? (
          <select
            className={styles.select}
            name="contract_id"
            defaultValue={flight?.contract_id}
          >
            <option className={styles.option}>Выберите</option>
            {contracts.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                <>{`${item.name} от ${item.date_start}`}</>
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.skeleton}>Загрузка списка...</div>
        )}
        <div id="contract_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.contract_id &&
            state.errors.contract_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="number">Номер заявки</label>
        <input
          className={styles.input}
          name="number"
          placeholder="Номер заявки"
          defaultValue={flight?.number}
        />
        <div id="number-error" aria-live="polite" aria-atomic="true">
          {state.errors?.number &&
            state.errors.number.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="application_date">Дата заявки</label>
        <input
          className={styles.input}
          type="date"
          name="application_date"
          placeholder="Дата заявки"
          defaultValue={flight?.application_date}
        />
        <div id="application_date-error" aria-live="polite" aria-atomic="true">
          {state.errors?.application_date &&
            state.errors.application_date.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="date_start">Дата и время вылета</label>
        <input
          className={styles.input}
          type="datetime-local"
          name="date_start"
          placeholder="Дата и время вылета"
          onChange={dateStartHandler}
          defaultValue={validateTime(flight?.date_start)}
        />
        <div id="date_start-error" aria-live="polite" aria-atomic="true">
          {state.errors?.date_start &&
            state.errors.date_start.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="date_end">Дата и время прилёта</label>
        <input
          className={styles.input}
          type="datetime-local"
          name="date_end"
          placeholder="Дата и время прилёта"
          onChange={dateEndHandler}
          defaultValue={validateTime(flight?.date_end)}
        />
        <div id="date_end-error" aria-live="polite" aria-atomic="true">
          {state.errors?.date_end &&
            state.errors.date_end.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="duration">Подолжительность</label>
        <input
          className={styles.input}
          disabled
          name="duration"
          placeholder="Расчитывается автоматически"
          value={diff}
        />
      </div>
      <div className={styles.row}>
        <label htmlFor="transport_type">Тип ВС</label>
        {typeOptions ? (
          <select
            className={styles.select}
            name="transport_type"
            value={type || undefined}
            onChange={(e) => typeHandler(e.target.value)}
          >
            <option value="">Выберите из списка</option>
            {typeOptions.map((unit) => (
              <option value={unit.type} key={unit.id}>
                {unit.type_name}
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.skeleton}>Загрузка списка...</div>
        )}
      </div>
      <div className={styles.row}>
        <label htmlFor="transport_id">Бортовой номер</label>
        {flight && numberOptions ? (
          <select
            className={styles.select}
            name="transport_id"
            onChange={(e) => numberHandler(e.target.value)}
            defaultValue={flight?.transport_id}
          >
            <option className={styles.option}>Выберите</option>
            {numberOptions.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.number}
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.skeleton}>Загрузка списка...</div>
        )}
        <div id="transport_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.transport_id &&
            state.errors.transport_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isAirbaseError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список авиаотделений.
        </Snackbar>
      )}
      {isFlightFetching || isAirbaseFetching || isContractFetching || isTransportFetching && <PreloaderBar />}
    </form>
  );
};

export const EditFlight: FC<Props> = ({ close, id }) => {
  return (
    <Modal close={close}>
      <Form close={close} id={id} />
    </Modal>
  );
};
